import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { ROLE } from '@/common/role';
import { DeletedItemBanner } from '@/components/soft-delete/DeletedItemBanner';
import { RestoreItemButton } from '@/components/soft-delete/RestoreItemButton';
import { useDeletedPreview } from '@/hooks/use-deleted-preview';
import { softDeleteTargets } from '@/queries/soft-delete/soft-delete';
import type { WithDeletedBy } from '@/common/soft-delete';
import type { AssetDetailType } from '@/common/asset';
import { AssetIcon, InspectionIcon } from '@/components/entity-icons';
import { usePermission } from '@/hooks/use-permission';
import { formatDateOnly } from '@/lib/utils';
import { tabIntoForm } from '@/lib/utils/focus';
import { getAssetOptions } from '@/queries/assets/get-asset';

import { DataUploadListContainer } from '../../data-upload/data-upload-list/page';
import { InspectionList } from '../../inspection/inspection-list/page';
import { SubAssetList } from '../../sub-asset/sub-asset-list/page';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { AssetOverview } from './overview';
import { AssetUsersListContainer } from './users/AssetUserListContainer';

export function AssetDetail() {
	const { t } = useTranslation();
	const { assetId } = useParams<{ assetId: string }>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const isNewAsset = assetId === 'new';
	const currenTab = searchParams.get('tab');

	// A deleted asset opened from the deleted list: read-only, overview only (GH #331)
	const isDeleted = useDeletedPreview('asset');
	const { data: assetDetailResponse, isLoading } = useQuery(
		getAssetOptions(assetId || '', true, { deleted: isDeleted }),
	);

	const asset = (assetDetailResponse || null) as WithDeletedBy<AssetDetailType> | null;

	const [assetName, setAssetName] = useState<string>(isNewAsset ? t('assets.nav.newAsset') : '');
	const nameInputRef = useRef<HTMLInputElement>(null);

	// A new asset starts with the cursor in its name
	useEffect(() => {
		if (isNewAsset) nameInputRef.current?.select();
	}, [isNewAsset]);

	const [activeTab, setActiveTab] = useState(currenTab || 'overview');

	const hasUpdatePermission = usePermission(ROLE.ASSETS_UPDATE);
	const hasInspectionReadPermission = usePermission(ROLE.INSPECTIONS_READ);
	const hasDataUploadsReadPermission = usePermission(ROLE.DATA_UPLOADS_READ);
	const hasAssetUserReadPermission = usePermission(ROLE.ASSET_USERS_READ);
	const hasSubAssetsReadPermission = usePermission(ROLE.SUB_ASSETS_READ);

	const tabList = [
		{ value: 'overview', label: t('assets.tabs.overview') },
		{
			value: 'inspections',
			label: t('assets.tabs.inspections', { count: asset?.inspectionCount || 0 }),
			disabled: isNewAsset,
			hidden: !hasInspectionReadPermission,
		},
		{
			value: 'structure',
			label: t('assets.tabs.structure'),
			disabled: isNewAsset,
			hidden: !hasSubAssetsReadPermission,
		},
		{
			value: 'data_upload',
			label: t('assets.tabs.dataUpload'),
			disabled: isNewAsset,
			hidden: !hasDataUploadsReadPermission,
		},
		{ value: 'users', label: t('assets.tabs.users'), disabled: isNewAsset, hidden: !hasAssetUserReadPermission },
		// Its inspections, structure and files were deleted with it; the restore dialog says what comes back
	].filter((tab) => !isDeleted || tab.value === 'overview');
	const onAssetNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setAssetName(e.target.value);
	};
	// Sync asset name when asset data is loaded or updated
	useEffect(() => {
		if (asset?.name) {
			setAssetName(asset.name);
		}
	}, [asset?.name]);

	if (isLoading || !assetId) return <div className="p-6">{t('common.states.loading')}</div>;

	if (!isLoading && !asset && assetId !== 'new') return <div className="p-6">{t('assets.detail.notFound')}</div>;
	return (
		<>
			<div className="border-b text-card-foreground bg-card flex-none">
				<div className="p-3 pb-2 md:p-6 md:pb-4">
					<Button
						variant="ghost"
						className="text-muted-foreground hover:text-primary hover:bg-transparent! px-0! mb-1 md:mb-2 gap-0 hover:pl-2! cursor-pointer text-xs md:text-sm"
						onClick={() => navigate(isNewAsset || isDeleted ? '/' : `/?selected=${assetId}`)}
					>
						<ArrowLeft className="size-3 md:size-4 mr-1!" />
						{t('common.actions.backToList')}
					</Button>
					<div className="flex justify-between items-center gap-2 mb-1 md:mb-4">
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-2">
								<AssetIcon
									className="size-5 md:size-6 shrink-0 text-muted-foreground"
									aria-label={t('assets.card.iconLabel')}
								/>
								<Input
									ref={nameInputRef}
									type="text"
									onChange={onAssetNameChange}
									onKeyDown={tabIntoForm}
									value={assetName}
									className="border-transparent hover:border-foreground/20 dark:bg-transparent text-base md:text-2xl font-bold px-2 py-0.5 md:py-1 h-auto w-full shadow-none"
									disabled={!hasUpdatePermission}
								/>
							</div>
						</div>
						{/* Shortcut to the most recent inspection */}
						{hasInspectionReadPermission && !isDeleted && asset?.latestInspection && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="outline"
										size="sm"
										className="shrink-0 bg-white h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3 cursor-pointer"
										onClick={() => navigate(`/assets/${assetId}/inspections/${asset.latestInspection?.id}`)}
									>
										<InspectionIcon className="size-3.5 lg:size-4" />
										<span className="hidden sm:inline">{t('assets.list.lastInspection')}</span>
										<span className="text-muted-foreground font-normal">
											{formatDateOnly(asset.latestInspection.startDate)}
										</span>
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									{t('assets.list.openLatestInspection', { title: asset.latestInspection.title })}
								</TooltipContent>
							</Tooltip>
						)}
					</div>
				</div>

				{isDeleted && asset && (
					<DeletedItemBanner
						className="mx-3 mb-3 md:mx-6"
						itemLabel={t('common.entities.asset', { count: 1 })}
						deletedAt={asset.deletedAt}
						deletedBy={asset.deletedBy}
					/>
				)}
				<Tabs value={activeTab} onValueChange={setActiveTab} className="gap-0">
					<TabsList className="w-full justify-start rounded-none border-b border-transparent bg-transparent p-0 overflow-x-auto">
						{tabList.map((tab) => (
							<TabsTrigger
								key={tab.value}
								className="rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary! data-[state=active]:bg-transparent data-[state=active]:text-primary! bg-transparent! text-muted-foreground hover:text-primary! hover:cursor-pointer px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm shrink-0"
								value={tab.value}
								disabled={tab.disabled}
								hidden={tab.hidden}
							>
								{tab.label}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</div>
			<div className="flex-1 overflow-auto bg-background text-foreground p-2 md:p-4">
				{(activeTab === 'overview' || isDeleted) && (
					<AssetOverview
						asset={asset}
						isNew={isNewAsset}
						assetName={assetName}
						restoreButton={
							isDeleted && asset ? (
								<RestoreItemButton
									target={softDeleteTargets.asset(assetId)}
									name={asset.name || t('common.entities.asset', { count: 1 })}
									restoreRole={ROLE.ASSETS_DELETE}
									onRestored={() => navigate(`/assets/${assetId}`, { replace: true })}
									className="h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3"
								/>
							) : undefined
						}
					/>
				)}
				{!isDeleted && activeTab === 'inspections' && <InspectionList assetId={assetId as string} />}
				{!isDeleted && activeTab === 'structure' && <SubAssetList asset={asset} />}
				{!isDeleted && activeTab === 'data_upload' && <DataUploadListContainer type="asset" assetId={assetId} />}
				{!isDeleted && activeTab === 'users' && (
					<AssetUsersListContainer assetId={assetId} organizationId={asset?.organizationId as string} />
				)}
			</div>
		</>
	);
}
